import { useContext, useEffect, useState } from "react";
import { ButtonDS } from "get-life-storybook-ts";
import { I18nContext } from "../../../../../../context/i18n.context";
import { cardStyle } from "../cardStyles";
import { Beneficiary, RELATIONSHIPS, inputStyle, labelStyle } from "./constants";

interface BeneficiaryFormProps {
  editing: Beneficiary | null;
  onSave: (value: Omit<Beneficiary, "id"> & { id?: string }) => void;
  onCancel: () => void;
}

const BeneficiaryForm = ({ editing, onSave, onCancel }: BeneficiaryFormProps) => {
  const {
    state: { translate },
  } = useContext(I18nContext);

  const [name, setName] = useState(editing?.name ?? "");
  const [dni, setDni] = useState(editing?.dni ?? "");
  const [relationship, setRelationship] = useState(editing?.relationship ?? "spouse");
  const [percentage, setPercentage] = useState<number>(editing?.percentage ?? 0);

  useEffect(() => {
    setName(editing?.name ?? "");
    setDni(editing?.dni ?? "");
    setRelationship(editing?.relationship ?? "spouse");
    setPercentage(editing?.percentage ?? 0);
  }, [editing]);

  const disabled = !name.trim() || percentage <= 0;

  const handleSave = () => {
    if (disabled) return;
    onSave({
      id: editing?.id,
      name,
      dni,
      relationship,
      percentage,
    });
  };

  return (
    <div style={{ ...cardStyle, border: "2px solid #e0e7ff" }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, color: "#111827", margin: "0 0 20px" }}>
        {editing
          ? translate("newDashboard.beneficiaryManager.edit")
          : translate("newDashboard.beneficiaryManager.addButton")}
      </h3>

      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
        className="max-[600px]:!grid-cols-1"
      >
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={labelStyle}>
            {translate("newDashboard.beneficiaryManager.name")}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={inputStyle}
            placeholder="Ej: María López García"
          />
        </div>

        <div>
          <label style={labelStyle}>
            {translate("newDashboard.beneficiaryManager.dni")}
          </label>
          <input
            type="text"
            value={dni}
            onChange={(e) => setDni(e.target.value)}
            style={inputStyle}
            placeholder="12345678A"
          />
        </div>

        <div>
          <label style={labelStyle}>
            {translate("newDashboard.beneficiaryManager.relationship")}
          </label>
          <select
            value={relationship}
            onChange={(e) => setRelationship(e.target.value)}
            style={{ ...inputStyle, appearance: "auto" as any }}
          >
            {RELATIONSHIPS.map((r) => (
              <option key={r.value} value={r.value}>
                {translate(r.key)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle}>
            {translate("newDashboard.beneficiaryManager.percentage")}
          </label>
          <div style={{ position: "relative" }}>
            <input
              type="number"
              min={1}
              max={100}
              value={percentage || ""}
              onChange={(e) =>
                setPercentage(Math.max(0, Math.min(100, Number(e.target.value))))
              }
              style={{ ...inputStyle, paddingRight: 36 }}
              placeholder="0"
            />
            <span
              style={{
                position: "absolute",
                right: 14,
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: 15,
                fontWeight: 600,
                color: "#9ca3af",
              }}
            >
              %
            </span>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, marginTop: 20 }}>
        <ButtonDS
          label={
            editing
              ? translate("newDashboard.beneficiaryManager.save")
              : translate("newDashboard.beneficiaryManager.addButton")
          }
          buttonType="primary"
          size="40"
          onClick={handleSave}
          disabled={disabled}
        />
        <ButtonDS
          label={translate("newDashboard.beneficiaryManager.cancel")}
          buttonType="secondary"
          size="40"
          onClick={onCancel}
        />
      </div>
    </div>
  );
};

export default BeneficiaryForm;
